import {
    Component,
    ViewChild,
    Input,
    OnInit,
    EventEmitter,
    Output,
    AfterViewInit,
    TemplateRef
} from '@angular/core';
import {
    FormGroup,
    FormControl,
    FormArray,
    ValidatorFn,
    ValidationErrors,
    Validators
} from '@angular/forms';

import { Subscription, pairwise} from 'rxjs';
import { indexOf } from 'lodash';

import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { NetworkTypeEnum, networkOptionsOrder as networkOrder, networkTypeIconMap } from '../../../Core/enums/network-type.enum';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';

import { GeoRadiusUnit } from '../../../../Common/Misc/models/geographical-circle.model';
import { InputValidationModel } from '../../../../Common/Forms/models/input-validation.model';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';

import { DataSelectDialogModel } from '../../../../Common/Dialogs/models/data-select-dialog.model';
import { DataSelectDialogComponent } from '../../../../Common/Dialogs/components/DataSelectDialog/data-select-dialog.component';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { AutocompleteChipsOptions } from '../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import { CountryApiModel } from '@api/journodex/model/country-api.model';
import { TargetingSearchLanguageSelectDialogService } from '../../../../Common/TargetingShared/services/targeting-search-language-select-dialog.service';
import { LanguageModel } from '../../../../Core/Localization/models/language.model';

interface NetworkItem {
    type: NetworkTypeEnum;
    label: string;
    icon: string;
}

@Component({
    selector: 'agpr-agent-search-settings',
    templateUrl: './agent-search-settings.component.html',
    styleUrls: ['./agent-search-settings.component.scss'],
    providers: [TargetingSearchLanguageSelectDialogService],
    standalone: false
})

export class AgentSearchSettingsComponent implements OnInit, AfterViewInit  {
    @Input() set networks (value: NetworkTypeEnum[]) {
        if (value) {
            this._networks = [];
            value.forEach(network => {
                this._networks.push({
                    type: network,
                    label: `Social.intelligence.network.${network}`,
                    icon: this.networkTypeIconMap.get(network)
                });
            });
            this._networks.sort((a, b) => {
                return indexOf(networkOrder, a.type) - indexOf(networkOrder, b.type);
            });
        }
    }
    @Input() agent: MediaSubscription;
    @Input() set disabled (value: boolean) {
        this._disabled = value;
        if (value) {
            this.settingsForm?.disable();
        } else {
            this.settingsForm?.enable();
        }
    }
    _disabled: boolean = false;

    _networks: NetworkItem[] = [];

    @Input() disableSampleRate: boolean = false;

    @Output() change: EventEmitter<any> = new EventEmitter<any>();
    @ViewChild('noMatchingLanguageMessageTemplate') noMatchingLanguageMessageTemplate: TemplateRef<any>;

    id: string = 'agentSearchSettings';

    radiusTooltip: string[] = [
        'Social.agentSearchSettings.twitter.geoPicker.tooltip.point1',
        'Social.agentSearchSettings.twitter.geoPicker.tooltip.point2',
        'Social.agentSearchSettings.twitter.geoPicker.tooltip.point3'
    ];
    settingsForm: FormGroup = new FormGroup({
        twitter: new FormGroup({
            sampleRate: new FormControl<number>({
                value: 100, disabled: false
            }),
            languages: new FormControl(
                new IncludeExcludeModel<LanguageModel>
            ),
            showVerifiedOptions: new FormControl<boolean>(false),
            verifiedAuthor: new FormControl<boolean>(null),
            countries: new FormControl<CountryApiModel[]>([]),
            geoCircles: new FormArray([])
        })
    });

    noContentMessages: { [key: string]: AutocompleteCustomOption } = {};

    minimumRadiusValue: number = 0.01;

    readonly chipValidations = [ new InputValidationModel({
        errorKey: 'Monitoring.keyWords.minimumKeywordCharacterLength',
        isValid: (input: string): boolean => {
            return input.trim().length > 1;
        }
    })];
    readonly countryAutocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.journodexRegionsService.autoCompleteCountryWithCode.bind(this.journodexRegionsService),
        dropdown: true,
        forceSelection: true,
    };

    readonly buttonTypes = ButtonTypesEnum;
    readonly geoRadiusUnit = GeoRadiusUnit;
    readonly networkTypes = NetworkTypeEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly networkTypeIconMap = networkTypeIconMap;

    subscriptions: Subscription[] = [];

    constructor(
        public localizationService: LocalizationService,
        private languageSelectDialogService: TargetingSearchLanguageSelectDialogService,
        private coreDialogService: CoreDialogService,
        public journodexRegionsService: JournodexRegionsService,
    ) {
        this.subscriptions.push(
            this.showVerifiedOptions.valueChanges.subscribe(showVerifiedOptions => {
                if (showVerifiedOptions && this.verifiedAuthor.value === null) {
                    this.verifiedAuthor.patchValue(true, { emitEvent: false });
                }
            }),
            this.verifiedAuthor.valueChanges.subscribe(verifiedAuthor => {
                // use patch value here to cause radio component to write value and update checked state
                this.verifiedAuthor.patchValue(verifiedAuthor, { emitEvent: false });
            }),
            this.settingsForm.get('twitter').valueChanges.subscribe(settings => {
                this.change.emit(settings);
            }),

            this.geoCircles.valueChanges.pipe(pairwise()).subscribe(([prev, curr]) => {

                this.geoCircles.controls.forEach((control, index) => {
                    const radius = control.get('radius');
                    const maximumRadiusValue = curr[index].radiusUnit === GeoRadiusUnit.km ? 40.23 : 25;
                    control.get('maximumRadiusValue').patchValue(maximumRadiusValue, {emitEvent: false});
                    control.get('radiusUnit').patchValue(curr[index].radiusUnit, {emitEvent: false});

                    if (curr[index].radius && prev[index]?.radiusUnit && curr[index].radiusUnit !== prev[index].radiusUnit) {
                        // convert from mile to km
                        if (curr[index].radiusUnit === GeoRadiusUnit.km) {
                            radius.patchValue((curr[index].radius * 1.609344).toFixed(2), {emitEvent: false});
                        } else {
                            // convert from km to mile
                            radius.patchValue((curr[index].radius * 0.621371).toFixed(2), {emitEvent: false});
                        }
                    } else {
                        radius.patchValue((curr[index].radius), {emitEvent: false});
                    }
                });
            }),
        );

    }

    ngOnInit() {
        if (this.agent?.id) {
            const languageIncludeExclude = new IncludeExcludeModel<LanguageModel>({
                include: this.agent.languageFilter?.include?.map(x => new LanguageModel(x)) ?? [],
                exclude: this.agent.languageFilter?.exclude?.map(x => new LanguageModel(x)) ?? []
            });

            this.settingsForm.patchValue({
                twitter: {
                    sampleRate: this.agent.samplePercentage,
                    languages: languageIncludeExclude,
                    showVerifiedOptions: this.agent.verifiedAuthor === null ? false : true,
                    verifiedAuthor: this.agent.verifiedAuthor,
                    countries: this.agent.countryCodes.map(code => this.journodexRegionsService.getCountryByIsoCode(code)) ?? []
                }
            });
            this.agent.geoCircles?.forEach(geoCircle => {
                this.geoCircles.push(new FormGroup({
                    latitude: new FormControl<number>(geoCircle.latitude, Validators.required),
                    longitude: new FormControl<number>(geoCircle.longitude, Validators.required),
                    radius: new FormControl<number>(geoCircle.radius),
                    radiusUnit: new FormControl<GeoRadiusUnit>(geoCircle.radiusUnit),
                    maximumRadiusValue: new FormControl<number>(geoCircle.radiusUnit === GeoRadiusUnit.km ? 40.23 : 25)
                }, {
                    validators: [this.validateGeoCoordinateValue(), this.validateGeoCoordinateFormat()]
                }));
            });
        }

    }

    ngAfterViewInit(): void {
        requestAnimationFrame(() => {
            this.prepareNoMatchingLanguageMessage();
        });
    }

    prepareNoMatchingLanguageMessage(): void {
        const customOption: AutocompleteCustomOption = {
            appearCondition: AppearCondition.noSuggestions,
            clearOnSelect: false,
            action: () => {},
            labelTemplate: this.noMatchingLanguageMessageTemplate,
            labelTemplateVariable: {
                severity: messageSeverityEnum.noContent,
                noContentMessageLabel: 'Discover.targeting.searchFieldLabels.noContentMessage.language'
            }

        };
        this.noContentMessages['languages'] = customOption;

    }

    showLanguageBrowser(includeOrExclude: IncludeOrExclude) {
        const languages = this.settingsForm.get('twitter.languages').value[includeOrExclude];
        this.languageSelectDialogService.prepareModelData(includeOrExclude, languages);

        const io: DataSelectDialogModel = {
            inputs: {
                id: 'targetingSearch.languageSelectDialog',
                dialogService: this.languageSelectDialogService,
                dialogHeader: 'Monitoring.result.languages',
                dialogMessage: 'Monitoring.result.selectLanguagesMsg',
                leftColumnHeader: 'Monitoring.result.availableLanguages',
                rightColumnHeader: 'Monitoring.result.selectedLanguages',
            },
            outputs: {
                dialogClose: (selectedLanguages: LanguageModel[]) => {
                    if (!selectedLanguages) {
                        selectedLanguages = [];
                    }
                    const newLang: IncludeExcludeModel<LanguageModel> = this.settingsForm.get('twitter.languages').value;
                    newLang[includeOrExclude] = selectedLanguages;

                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    this.settingsForm.patchValue({'twitter.languages': newLang});
                }
            }
        };
        this.coreDialogService.open(DataSelectDialogComponent, io);
    }

    addNewGeoCircle() {
        this.geoCircles.push(new FormGroup({
            latitude: new FormControl<number>(null,  Validators.required),
            longitude: new FormControl<number>(null, Validators.required),
            radius: new FormControl<number>(10),
            radiusUnit: new FormControl<GeoRadiusUnit>(GeoRadiusUnit.mile),
            maximumRadiusValue: new FormControl<number>(25)
        }, {
            validators: [this.validateGeoCoordinateValue(), this.validateGeoCoordinateFormat()]
        }));
    }

    deleteGeoCircle(index: number) {
        this.geoCircles.removeAt(index);
    }

    validateGeoCoordinateValue(): ValidatorFn {
        return (group: FormGroup): ValidationErrors | null => {
            const latitude = group.get('latitude').value;
            const longitude = group.get('longitude').value;

            // valid value range: -90 to 90
            const validLatitudeValue: boolean = latitude >= -90 && latitude <= 90;
            // valid value range: -180 to 180
            const validLongitudeValue: boolean = longitude >= -180 && longitude <= 180;

            return !validLatitudeValue ||  !validLongitudeValue? { invalidValue: true }  : null;
        };
    }

    validateGeoCoordinateFormat() {
        return (group: FormGroup): ValidationErrors | null => {
            const latitude = group.get('latitude').value;
            const longitude = group.get('longitude').value;

            let validLatitudeFormat: boolean = true;
            let validLongitudeFormat: boolean = true;


            if (latitude) {
                if (Math.floor(latitude) === latitude) {
                    validLatitudeFormat = true;
                } else {
                // allow up to 14 decimal places
                    validLatitudeFormat = latitude.toString().split('.')[1].length <= 14;
                }

            }

            if (longitude) {
                if (Math.floor(longitude) === longitude) {
                    validLongitudeFormat = true;
                } else {
                    // allow up to 14 decimal places
                    validLongitudeFormat = longitude.toString().split('.')[1].length <= 14;
                }
            }


            return !validLatitudeFormat ||  !validLongitudeFormat? { invalidFormat: true }  : null;
        };
    }


    get showVerifiedOptions(): FormControl {
        return this.settingsForm.get('twitter.showVerifiedOptions') as FormControl;
    }

    get verifiedAuthor(): FormControl {
        return this.settingsForm.get('twitter.verifiedAuthor') as FormControl;
    }


    get geoCircles(): FormArray {
        return this.settingsForm.get('twitter.geoCircles') as FormArray;
    }

    isFormValidated(): boolean {
        this.geoCircles.controls.forEach(x => {
            x.get('longitude').markAsDirty();
            x.get('longitude').updateValueAndValidity();

            x.get('latitude').markAsDirty();
            x.get('latitude').updateValueAndValidity();
        });

        return this.settingsForm.status === 'VALID';
    }
}
